import { useState, useEffect } from 'react';
import { Preferences } from '@capacitor/preferences';

const PRESETS_KEY = 'api_url_presets';
const ACTIVE_PRESET_KEY = 'active_preset_id';

/**
 * Hook to manage API URL Presets
 * Allows users to save multiple server URLs and quickly switch between them
 */
export const useApiPresets = () => {
  const [presets, setPresets] = useState([]);
  const [activePresetId, setActivePresetId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Default Presets
  const defaultPresets = [
    {
      id: 'production',
      name: 'Production Server',
      url: 'https://api.billard.flogra.de',
      description: 'Offizieller Production Server',
      isDefault: true,
    },
    {
      id: 'local',
      name: 'Local Development',
      url: 'http://localhost:86',
      description: 'Lokaler Entwicklungsserver',
      isDefault: true,
    },
  ];

  // Load presets on mount
  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      const { value: presetsJson } = await Preferences.get({ key: PRESETS_KEY });
      const { value: activeId } = await Preferences.get({ key: ACTIVE_PRESET_KEY });

      if (presetsJson) {
        const loadedPresets = JSON.parse(presetsJson);
        setPresets(loadedPresets);
      } else {
        // Initialize with default presets
        setPresets(defaultPresets);
        await savePresetsToStorage(defaultPresets);
      }

      if (activeId) {
        setActivePresetId(activeId);
      }
    } catch (error) {
      console.error('Error loading presets:', error);
      setPresets(defaultPresets);
    } finally {
      setIsLoading(false);
    }
  };

  const savePresetsToStorage = async (presetsToSave) => {
    try {
      await Preferences.set({
        key: PRESETS_KEY,
        value: JSON.stringify(presetsToSave),
      });
    } catch (error) {
      console.error('Error saving presets:', error);
      throw error;
    }
  };

  const addPreset = async (preset) => {
    try {
      const newPreset = {
        id: `preset_${Date.now()}`,
        name: preset.name,
        url: preset.url.replace(/\/$/, ''), // Remove trailing slash
        description: preset.description || '',
        isDefault: false,
        createdAt: new Date().toISOString(),
      };

      const updatedPresets = [...presets, newPreset];
      setPresets(updatedPresets);
      await savePresetsToStorage(updatedPresets);

      return { success: true, preset: newPreset };
    } catch (error) {
      console.error('Error adding preset:', error);
      return { success: false, error: error.message };
    }
  };

  const updatePreset = async (presetId, updates) => {
    try {
      const updatedPresets = presets.map((preset) =>
        preset.id === presetId
          ? {
              ...preset,
              ...updates,
              url: updates.url ? updates.url.replace(/\/$/, '') : preset.url,
            }
          : preset
      );

      setPresets(updatedPresets);
      await savePresetsToStorage(updatedPresets);

      return { success: true };
    } catch (error) {
      console.error('Error updating preset:', error);
      return { success: false, error: error.message };
    }
  };

  const deletePreset = async (presetId) => {
    try {
      // Prevent deleting the active preset
      if (presetId === activePresetId) {
        return {
          success: false,
          error: 'Cannot delete active preset. Switch to another preset first.',
        };
      }

      const preset = presets.find((p) => p.id === presetId);
      if (preset?.isDefault) {
        return {
          success: false,
          error: 'Cannot delete default presets.',
        };
      }

      const updatedPresets = presets.filter((preset) => preset.id !== presetId);
      setPresets(updatedPresets);
      await savePresetsToStorage(updatedPresets);

      return { success: true };
    } catch (error) {
      console.error('Error deleting preset:', error);
      return { success: false, error: error.message };
    }
  };

  const activatePreset = async (presetId) => {
    try {
      const preset = presets.find((p) => p.id === presetId);
      if (!preset) {
        return { success: false, error: 'Preset not found' };
      }

      await Preferences.set({
        key: ACTIVE_PRESET_KEY,
        value: presetId,
      });

      setActivePresetId(presetId);

      return { success: true, preset };
    } catch (error) {
      console.error('Error activating preset:', error);
      return { success: false, error: error.message };
    }
  };

  const getActivePreset = () => {
    if (!activePresetId) return null;
    return presets.find((p) => p.id === activePresetId);
  };

  const resetToDefaults = async () => {
    try {
      setPresets(defaultPresets);
      await savePresetsToStorage(defaultPresets);
      setActivePresetId(null);
      await Preferences.remove({ key: ACTIVE_PRESET_KEY });

      return { success: true };
    } catch (error) {
      console.error('Error resetting presets:', error);
      return { success: false, error: error.message };
    }
  };

  return {
    presets,
    activePresetId,
    activePreset: getActivePreset(),
    isLoading,
    addPreset,
    updatePreset,
    deletePreset,
    activatePreset,
    resetToDefaults,
  };
};
